class PerformanceImageSlider {
  constructor(section) {
    this.section = section;
    this.track = section.querySelector('[data-slider-track]');
    this.slides = Array.from(
      section.querySelectorAll('[data-slider-slide]')
    );
    this.previousButton = section.querySelector(
      '[data-slider-previous]'
    );
    this.nextButton = section.querySelector('[data-slider-next]');
    this.dots = Array.from(
      section.querySelectorAll('[data-slider-dot]')
    );
    this.autoplayButton = section.querySelector(
      '[data-slider-autoplay-toggle]'
    );

    this.currentIndex = 0;
    this.autoplayTimer = null;
    this.autoplayEnabled =
      section.dataset.autoplay === 'true';
    this.autoplayDelay =
      Number(section.dataset.autoplayDelay) || 5000;
    this.pauseOnHover =
      section.dataset.pauseOnHover === 'true';
    this.userPaused = false;
    this.scrollFrame = null;

    if (!this.track || this.slides.length < 1) return;

    this.bindEvents();
    this.updateSlider(0, false);
    this.startAutoplay();
  }

  bindEvents() {
    this.previousButton?.addEventListener('click', () => {
      this.goToSlide(this.currentIndex - 1);
    });

    this.nextButton?.addEventListener('click', () => {
      this.goToSlide(this.currentIndex + 1);
    });

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.goToSlide(index);
      });
    });

    this.track.addEventListener(
      'scroll',
      () => {
        if (this.scrollFrame) {
          cancelAnimationFrame(this.scrollFrame);
        }

        this.scrollFrame = requestAnimationFrame(() => {
          this.updateFromScroll();
        });
      },
      { passive: true }
    );

    this.autoplayButton?.addEventListener('click', () => {
      this.userPaused = !this.userPaused;
      this.updateAutoplayButton();

      if (this.userPaused) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    });

    if (this.pauseOnHover) {
      this.section.addEventListener('mouseenter', () => {
        this.stopAutoplay();
      });

      this.section.addEventListener('mouseleave', () => {
        if (!this.userPaused) {
          this.startAutoplay();
        }
      });
    }

    this.section.addEventListener('focusin', () => {
      this.stopAutoplay();
    });

    this.section.addEventListener('focusout', (event) => {
      if (
        !this.section.contains(event.relatedTarget) &&
        !this.userPaused
      ) {
        this.startAutoplay();
      }
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.stopAutoplay();
      } else if (!this.userPaused) {
        this.startAutoplay();
      }
    });
  }

  normalizeIndex(index) {
    if (index < 0) return this.slides.length - 1;
    if (index >= this.slides.length) return 0;
    return index;
  }

    goToSlide(index) {
      const normalizedIndex = this.normalizeIndex(index);
      const slide = this.slides[normalizedIndex];

      if (!slide || !this.track) return;

        const reduceMotion = window.matchMedia(
         '(prefers-reduced-motion: reduce)'
         ).matches;

       this.track.scrollTo({
      left: slide.offsetLeft,
      top: 0,
      behavior: reduceMotion ? 'auto' : 'smooth'
     });

     this.updateSlider(normalizedIndex, true);
     }

  updateFromScroll() {
    const slideWidth = this.track.clientWidth;

    if (!slideWidth) return;

    const newIndex = Math.round(
      this.track.scrollLeft / slideWidth
    );

    if (
      newIndex !== this.currentIndex &&
      newIndex >= 0 &&
      newIndex < this.slides.length
    ) {
      this.updateSlider(newIndex, false);
    }
  }

  updateSlider(index, restartAutoplay) {
    this.currentIndex = index;

    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === index;

      slide.classList.toggle('is-active', active);
      slide.setAttribute(
        'aria-hidden',
        active ? 'false' : 'true'
      );
    });

    this.dots.forEach((dot, dotIndex) => {
      const active = dotIndex === index;

      dot.classList.toggle('is-active', active);
      dot.setAttribute(
        'aria-current',
        active ? 'true' : 'false'
      );
    });

    if (restartAutoplay && !this.userPaused) {
      this.startAutoplay();
    }
  }

  startAutoplay() {
    this.stopAutoplay();

    if (
      !this.autoplayEnabled ||
      this.userPaused ||
      this.slides.length < 2
    ) {
      return;
    }

    this.autoplayTimer = window.setInterval(() => {
      this.goToSlide(this.currentIndex + 1);
    }, this.autoplayDelay);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  updateAutoplayButton() {
    if (!this.autoplayButton) return;

    const pauseIcon =
      this.autoplayButton.querySelector('[data-pause-icon]');
    const playIcon =
      this.autoplayButton.querySelector('[data-play-icon]');

    this.autoplayButton.setAttribute(
      'aria-pressed',
      this.userPaused ? 'true' : 'false'
    );

    this.autoplayButton.setAttribute(
      'aria-label',
      this.userPaused
        ? 'Play slideshow'
        : 'Pause slideshow'
    );

    if (pauseIcon) pauseIcon.hidden = this.userPaused;
    if (playIcon) playIcon.hidden = !this.userPaused;
  }

  destroy() {
    this.stopAutoplay();

    if (this.scrollFrame) {
      cancelAnimationFrame(this.scrollFrame);
    }
  }
}

const initializePerformanceSliders = (root = document) => {
  root
    .querySelectorAll('[data-performance-slider]')
    .forEach((section) => {
      if (section.dataset.sliderInitialized === 'true') {
        return;
      }

      section.dataset.sliderInitialized = 'true';
      section.performanceSlider =
        new PerformanceImageSlider(section);
    });
};

document.addEventListener('DOMContentLoaded', () => {
  initializePerformanceSliders();
});

document.addEventListener('shopify:section:load', (event) => {
  initializePerformanceSliders(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  const section = event.target.querySelector(
    '[data-performance-slider]'
  );

  section?.performanceSlider?.destroy();
});